import {Component, OnInit, inject, DestroyRef, EffectRef, effect, linkedSignal, Signal} from '@angular/core';
import {TranslatePipe} from "../../shared/pipes/translate.pipe";
import {CustomerService} from "../../shared/services/customer.service";
import {IconBarService} from "../../shared/services/icon-bar.service";
import {DatePipe, SlicePipe} from "@angular/common";
import {LanguageService} from "../../shared/services/language.service";
import {LoadingService} from "../../shared/services/loading.service";
import {HeadlineComponent} from "../../shared/elements/headline/headline.component";
import {IconBadgeComponent} from "../../shared/elements/icon-badge/icon-badge.component";
import {ApiService} from "../../shared/services/api.service";
import {LinkComponent} from "../../shared/elements/link/link.component";
import {WidgetCalendarComponent} from "../calendar/widget-calendar/widget-calendar.component";
import {NotificationComponent} from "../../shared/elements/notification/notification.component";
import {DialogHomeLinkComponent} from "./dialog-home-link/dialog-home-link.component";
import {DialogHomeLinkManagerComponent} from "./dialog-home-link-manager/dialog-home-link-manager.component";
import {DialogHomeIconsComponent} from "./dialog-home-icons/dialog-home-icons.component";
import {RouterService} from "../../shared/services/router.service";
import {LayoutService} from "../../shared/services/layout.service";
import {AvatarComponent} from "../../shared/elements/avatar/avatar.component";
import {NavService} from "../../shared/services/nav.service";
import {ContentComponent} from "../../shared/elements/content/content.component";
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {HomeFavorite} from '../../shared/types/home-types/user-favorites.interface';
import {Breakpoint} from '../../shared/types/home-types/breakpoint.type';
import {NewsTile} from '../../shared/types/home-types/newsTile.interface';
import {HomeLinksComponent} from './custom-links/custom-links.component';
import {NewsTilesComponent} from "./news-tiles/news-tiles.component";
import {CustomerIconComponent} from "../../shared/elements/customer-icon/customer-icon.component";
import {take} from 'rxjs';
import {ProfileService} from '../../shared/services/profile.service';
import {HomeFavoritesComponent} from "./user-favorites/user-favorites.component";
import {DashboardFavoriteResource} from '../../shared/types/generated/dashboardFavoriteResource';
import {DashboardLinkResource} from '../../shared/types/generated/dashboardLinkResource';
import {NotificationResource} from '../../shared/types/generated/notificationResource';
import {NotificationStateService} from '../../shared/services/notification-state.service';
import {CapabilitiesService} from "../../shared/services/capabilities.service";
import {DashboardLinksStateService} from '../../shared/services/dashboard-links-state.service';
import {SettingsMenuItemType} from "../../shared/types/generated/settingsMenuItemType";
import {IconBarPolicy} from "../../shared/services/icon-bar.service";
import {DialogHomeLinksAllComponent} from "../helper/bar-icons/dialog-home-links-all/dialog-home-links-all.component";
import {TYPE} from "../../shared/types/types";
import {ToasterService} from '../../shared/services/toaster.service';

@Component({
    selector: 'app-home',
    imports: [
    TranslatePipe,
    HeadlineComponent,
    DatePipe,
    IconBadgeComponent,
    LinkComponent,
    WidgetCalendarComponent,
    NotificationComponent,
    DialogHomeLinkComponent,
    DialogHomeLinkManagerComponent,
    DialogHomeIconsComponent,
    SlicePipe,
    AvatarComponent,
    ContentComponent,
    HomeLinksComponent,
    NewsTilesComponent,
    CustomerIconComponent,
    HomeFavoritesComponent,
    DialogHomeLinksAllComponent
],
    providers: [
        TranslatePipe
    ],
    templateUrl: './home.component.html',
    styleUrl: './home.component.scss'
})
export class HomeComponent implements OnInit{
  public loadingService: LoadingService = inject(LoadingService);
  public customerService: CustomerService = inject(CustomerService);
  public languageService: LanguageService = inject(LanguageService);
  public barIconService: IconBarService = inject(IconBarService);
  public routerService: RouterService = inject(RouterService);
  public layoutService: LayoutService = inject(LayoutService);
  public navService: NavService = inject(NavService);
  public profileService: ProfileService = inject(ProfileService);
  public capabilities: CapabilitiesService = inject(CapabilitiesService);
  private notificationStateService: NotificationStateService = inject(NotificationStateService);
  private apiService: ApiService = inject(ApiService);
  private toasterService: ToasterService = inject(ToasterService);
  private capabilitiesService: CapabilitiesService = inject(CapabilitiesService);
  private dashboardLinksStateService: DashboardLinksStateService = inject(DashboardLinksStateService);
  private readonly destroyRef: DestroyRef = inject(DestroyRef);

  public page: number = 1;
  public pageLinks: number = 1;
  public maxLinksPerPage: number = 8
  //status to open the icon customization dialog
  public homeIconsDialog: boolean = false;
  //status to open the link add dialog
  public homeLinkDialog: boolean = false;
  //status to open the link managament dialog
  public homeLinkManagerDialog: boolean = false;
  public homeLinksAllDialogVisible: boolean = false;
  public currentDate: Date = new Date();
  public hideHomeIcons: boolean = true;
  public currentBreakpoint: Breakpoint = 'lg';
  public favoritesPage:number = 1;
  public favoritesPageSize:number = 4;
  
  public newsTiles: NewsTile[] = [];
  public userFavorites: DashboardFavoriteResource[] = [];
  public dashboardLinks: DashboardLinkResource[] = [];
  public hasNewsTileData: boolean = true;
  private newsTilesComponentCreated: boolean = false;

  private favoritesEffect: EffectRef = effect((onCleanup) => {
    if(this.customerService.customerResource()?.home?.dashboard?.show_favorites) {
      const subscription = this.apiService.getDashboardFavoritesList()
      .pipe(take(1))
      .subscribe({
        next: (favs) => {
          this.userFavorites = favs;

          // Ensure pagination state is still valid when favorites count changes
          const totalFavPages = this.userFavorites.length
            ? Math.ceil(this.userFavorites.length / this.favoritesPageSize)
            : 1;
          if (this.favoritesPage > totalFavPages) {
            this.favoritesPage = totalFavPages;
          }
        },
        error: () => { this.userFavorites = []; },
      });
      onCleanup(() => subscription.unsubscribe());
    } else {
      this.userFavorites = [];
    }
  })

  private hasVisibleNewsTiles: Signal<boolean> = linkedSignal(() => {
    const tiles = this.customerService.customerResource()?.home?.dashboard?.tiles;
    const capabilities = this.capabilitiesService.capabilities();
    return !!tiles?.some((tile) => {
      if (!tile.enabled || !tile.type) {
        return false;
      }
      switch (tile.type) {
        case SettingsMenuItemType.NEWS:
          return capabilities.news !== null;
        case SettingsMenuItemType.KNOWLEDGEBASE:
          return capabilities.knowledgebase !== null;
        case SettingsMenuItemType.COMMUNITIES:
          return capabilities.communities !== null;
        default:
          return false;
      }
    });
  });

  private iconBarPolicyEffect: EffectRef = effect(() => {
    // Keep in sync when customer capability-driven tile visibility changes.
    this.hasVisibleNewsTiles();
    this.applyIconBarPolicy();
  });

  private dashboardLinksSyncEffect: EffectRef = effect(() => {
    this.dashboardLinks = this.dashboardLinksStateService.links();

    const totalPages = this.dashboardLinks.length
      ? Math.ceil(this.dashboardLinks.length / this.maxLinksPerPage)
      : 1;

    if (this.pageLinks > totalPages) {
      this.pageLinks = totalPages;
    }
  });

  public ngOnInit(): void {
    // breakpoint changes (xs..xxl)
    this.layoutService.breakpointWidthType
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((breakpoint) => {
      this.currentBreakpoint = breakpoint;

      // links per page depending on breakpoint
      this.maxLinksPerPage = (breakpoint === 'xs' || breakpoint === 'sm') ? 4 : 8;

      const totalLinks = this.dashboardLinks.length;
      const totalPages = totalLinks
        ? Math.ceil(totalLinks / this.maxLinksPerPage)
        : 1;

      if (this.pageLinks > totalPages) {
        this.pageLinks = totalPages;
      }

      //favorites
      if (breakpoint === 'xs' || breakpoint === 'sm') {
        this.favoritesPageSize = 2;
      } else if (breakpoint === 'md' || breakpoint === 'lg') {
        this.favoritesPageSize = 3;
      } else {
        this.favoritesPageSize = 4;
      }

      const totalFavs = this.userFavorites.length;
      const totalFavPages = totalFavs
        ? Math.ceil(totalFavs / this.favoritesPageSize)
        : 1;
      if (this.favoritesPage > totalFavPages) {
        this.favoritesPage = totalFavPages;
      }

      this.applyIconBarPolicy();
    });

    // initial data
    this.load();
    this.applyIconBarPolicy();
  }

  public load(): void {
    this.loadDashboardLinks(true);

    this.notificationStateService.loadLatestUnreadNotifications(
      25,
      (error) => {
        this.toasterService.error(
          this.languageService.getTranslationByKey('ERROR'),
          error
        );
      }
    );
  }

  private loadDashboardLinks(force = false): void {
    this.dashboardLinksStateService.loadLinks(force);
  }
  public get latestNotifications(): NotificationResource[] {
    return this.notificationStateService.latestNotifications();
  }

  public get iconsBadges(): Map<string, boolean> {
    return this.notificationStateService.iconsBadges();
  }

  public openLink(link: DashboardLinkResource): void {
    if (this.routerService.isDashboardLinkInternal(link.url)) {
      this.routerService.navigateTo(link.url);
      return;
    }

    this.routerService.openURL(link.url);
  }

  public get dashboardMenuItems(): TYPE.UserMenuItem[] {
    return this.navService
      .userMenu()
      .filter((item) => this.shouldRenderDashboardMenuItem(item));
  }

  public shouldRenderDashboardMenuItem(item: TYPE.UserMenuItem): boolean {
    if (!this.navService.isLinksMenuItem(item)) {
      return true;
    }

    return this.dashboardLinksStateService.links().length > 0;
  }

  public onDashboardMenuItemClick(item: TYPE.UserMenuItem): void {
    if (this.navService.isLinksMenuItem(item)) {
      this.homeLinksAllDialogVisible = true;
      return;
    }

    this.routerService.navigateTo(item.route);
  }


  public get showIconGrid(): boolean {
    // If no *visible* news tiles, ALWAYS show menu icons
    if (!this.hasVisibleNewsTiles()) {
      return true;
    }
    // Once the component has been created, don't destroy it just because it temporarily has no data
    // This prevents the circular dependency where the component gets destroyed while loading
    if (this.newsTilesComponentCreated) {
      return !this.hideHomeIcons || !this.isDesktopBreakpoint();
    }
    // Before first creation, check if there's data
    if (!this.hasNewsTileData) {
      return true;
    }

    // Otherwise: default behaviour
    return !this.hideHomeIcons || !this.isDesktopBreakpoint();
  }

  public get showLinksSection(): boolean {
    return !!this.customerService.customerResource()?.home?.dashboard?.show_links
  }

  public get showCalendarWidget(): boolean {
    return !!this.customerService.customerResource()?.home?.dashboard?.show_calendar
      && this.capabilitiesService.hasCalendarRight();
  }

  private isDesktopBreakpoint(): boolean {
    return this.currentBreakpoint === 'lg' || this.currentBreakpoint === 'xl' || this.currentBreakpoint === 'xxl';
  }

  public get showFavoritesSection(): boolean {
    return !!this.customerService.customerResource()?.home?.dashboard?.show_favorites && this.userFavorites && this.userFavorites.length > 0;
  }

  public onFavoriteClick(fav: HomeFavorite): void {
    if (fav.route) {
      this.routerService.navigateTo(fav.route);
    }
  }

  public onHomeLinkDialogChange(visible: boolean): void {
    this.homeLinkDialog = visible;
    if (!visible) {
      this.loadDashboardLinks(true);
    }
  }

  public onHomeLinkManagerDialogChange(visible: boolean): void {
    this.homeLinkManagerDialog = visible;
    if (!visible) {
      this.loadDashboardLinks(true);
    }
  }

  public onNewsTilesAvailabilityChange(hasData: boolean): void {
    this.newsTilesComponentCreated = true;
    this.hasNewsTileData = hasData;
    this.applyIconBarPolicy();
  }

  public markNotificationRead(notificationId: number): void {
    this.notificationStateService.markNotificationRead(
      notificationId,
      (err) => {
        console.error(err);
      }
    );
  }

  private applyIconBarPolicy(): void {
    if (this.showIconGrid) {
      this.barIconService.setIconBarPolicy(IconBarPolicy.Toggleable);
      return;
    }

    this.barIconService.setIconBarPolicy(IconBarPolicy.ForcedVisible);
  }

}









